<template>
  <div class="summary-carousel" :style="carouselStyle">
    <button v-if="!isTouchDevice" v-show="!isStartVisible" class="carousel-arrow prev" aria-label="View previous selected items" @click="scrollPrev">
      <Icon :icon="csgChevronSmallLeft" />
    </button>

    <div ref="viewport" class="carousel-viewport" @scroll="onScroll" @scrollend="onScrollEnd">
      <ul ref="track" class="carousel-track">
        <li v-for="(item, index) in list" :key="item.upcId" class="carousel-slide">
          <div class="carousel-slide-container">
            <button
              type="button"
              class="carousel-slide-button"
              :aria-label="`Selected item: ${item.alt}. View details`"
              @click="$emit('item-click', { item, index })"
              @keydown.enter="$emit('item-click', { item, index })"
              @keydown.space.prevent="$emit('item-click', { item, index })"
            ></button>
            <slot name="item" :data="item" :index="index"></slot>
          </div>
        </li>
      </ul>
    </div>

    <button v-if="!isTouchDevice" v-show="!isEndVisible" class="carousel-arrow next" aria-label="View next selected items" @click="scrollNext">
      <Icon :icon="csgChevronSmallRight" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { csgChevronSmallLeft, csgChevronSmallRight } from '@csgSvgIcons/icons';
import type { SelectedItem } from '~/stores/useCollectionSelectionStore';
import Icon from '@atomic-ui/icon/Icon.vue';
import { useDeviceType } from '~/composables/utils/useDeviceType';

interface Props {
  list: SelectedItem[];
  gapDesktop?: number;
  gapMobile?: number;
  offset?: number;
  stepByStep?: boolean;
  autoScrollToNew?: boolean;
  visibleSlidesDesktop?: number;
  visibleSlidesMedium?: number;
  visibleSlidesMobile?: number;
}

const props = withDefaults(defineProps<Props>(), {
  gapDesktop: 16,
  gapMobile: 8,
  offset: 16,
  stepByStep: true,
  autoScrollToNew: true,
  visibleSlidesDesktop: 3.5,
  visibleSlidesMedium: 9,
  visibleSlidesMobile: 4.5,
});

defineEmits<(e: 'item-click', payload: { item: Record<string, unknown>; index: number }) => void>();

const { isMobile, isTablet } = useDeviceType();

const viewport = ref<HTMLElement | null>(null);
const track = ref<HTMLElement | null>(null);

const isStartVisible = ref(true);
const isEndVisible = ref(true);
const isTouchDevice = ref(false);

const carouselStyle = computed(() => ({
  '--carousel-gap-desktop': `${props.gapDesktop}px`,
  '--carousel-gap-mobile': `${props.gapMobile}px`,
  '--carousel-offset': `${props.offset}px`,
  '--visible-slides-desktop': props.visibleSlidesDesktop,
  '--visible-slides-medium': props.visibleSlidesMedium,
  '--visible-slides-mobile': props.visibleSlidesMobile,
  '--visible-slides-desktop-gap-count': Math.ceil(props.visibleSlidesDesktop) - 1,
  '--visible-slides-medium-gap-count': Math.ceil(props.visibleSlidesMedium) - 1,
  '--visible-slides-mobile-gap-count': Math.ceil(props.visibleSlidesMobile) - 1,
}));

let scrollTimeout: ReturnType<typeof setTimeout>;

const updateArrowsVisibility = () => {
  if (!viewport.value || !track.value) {
    isStartVisible.value = true;
    isEndVisible.value = true;
    return;
  }

  const slides = Array.from(track.value.children) as HTMLElement[];
  if (slides.length === 0) {
    isStartVisible.value = true;
    isEndVisible.value = true;
    return;
  }

  const viewportRect = viewport.value.getBoundingClientRect();
  const firstSlideRect = slides[0]?.getBoundingClientRect();
  const lastSlideRect = slides[slides.length - 1]?.getBoundingClientRect();

  isStartVisible.value = (firstSlideRect?.left || 0) >= viewportRect.left - 2;
  isEndVisible.value = (lastSlideRect?.right || 0) <= viewportRect.right + 2;
};

const onScroll = () => {
  if (scrollTimeout) clearTimeout(scrollTimeout);

  scrollTimeout = setTimeout(() => {
    updateArrowsVisibility();
  }, 150);
};

const onScrollEnd = () => {
  if (scrollTimeout) clearTimeout(scrollTimeout);
  updateArrowsVisibility();
};

const scrollNext = () => {
  if (!viewport.value || !track.value || track.value.children.length === 0) return;
  const viewportRect = viewport.value.getBoundingClientRect();
  const slides = Array.from(track.value.children) as HTMLElement[];
  const slideWidth = slides?.[0]?.offsetWidth || 0;
  const gap = parseFloat(getComputedStyle(track.value).gap) || 0;
  const step = slideWidth + gap;

  const edgeSlide = slides.find((slide) => {
    const rect = slide.getBoundingClientRect();
    return rect.left < viewportRect.right - 1 && rect.right > viewportRect.right + 1;
  });

  if (edgeSlide) {
    const rect = edgeSlide.getBoundingClientRect();

    const distanceToRevealEdge = rect.right - viewportRect.right;
    const shift = distanceToRevealEdge + step;
    viewport.value.scrollBy({ left: shift, behavior: 'smooth' });
  } else {
    viewport.value.scrollBy({ left: step, behavior: 'smooth' });
  }
};

const scrollPrev = () => {
  if (!viewport.value || !track.value || track.value.children.length === 0) return;
  const viewportRect = viewport.value.getBoundingClientRect();
  const slides = Array.from(track.value.children) as HTMLElement[];
  const slideWidth = slides[0].offsetWidth;
  const gap = parseFloat(getComputedStyle(track.value).gap) || 0;
  const step = slideWidth + gap;

  const edgeSlide = slides.find((slide) => {
    const rect = slide.getBoundingClientRect();
    return rect.left < viewportRect.left - 1 && rect.right > viewportRect.left + 1;
  });

  if (edgeSlide) {
    const rect = edgeSlide.getBoundingClientRect();
    const distanceToRevealEdge = viewportRect.left - rect.left;
    const shift = distanceToRevealEdge + step;
    viewport.value.scrollBy({ left: -shift, behavior: 'smooth' });
  } else {
    viewport.value.scrollBy({ left: -step, behavior: 'smooth' });
  }
};

const scrollToIndex = (index: number, behavior: ScrollBehavior = 'smooth'): boolean => {
  if (!track.value || !viewport.value) return false;
  const slides = track.value.children;
  const target = slides[index] as HTMLElement;

  if (target) {
    const style = getComputedStyle(target);
    const marginLeft = parseFloat(style.marginLeft) || 0;
    const marginRight = parseFloat(style.marginRight) || 0;

    const targetLeft = target.offsetLeft - marginLeft;
    const targetRight = target.offsetLeft + target.offsetWidth + marginRight;

    const viewportLeft = viewport.value.scrollLeft;
    const viewportWidth = viewport.value.offsetWidth;
    const viewportRight = viewportLeft + viewportWidth;

    if (targetRight > viewportRight) {
      const gap = index < slides.length - 1 ? parseFloat(getComputedStyle(track.value).gap) || 0 : 0;
      viewport.value.scrollTo({ left: targetRight - viewportWidth + gap, behavior });
      return true;
    } else if (targetLeft < viewportLeft) {
      viewport.value.scrollTo({ left: targetLeft, behavior });
      return true;
    }
  }
  return false;
};

watch(
  () => props.list,
  (newVal, oldVal) => {
    nextTick(() => {
      let didScroll = false;

      if (props.autoScrollToNew) {
        const oldLen = oldVal?.length || 0;
        const newLen = newVal.length;

        if (newLen > oldLen) {
          didScroll = scrollToIndex(newLen - 1);
        } else if (newLen === oldLen) {
          const changedIndex = newVal.findIndex((item, i) => JSON.stringify(item) !== JSON.stringify(oldVal?.[i]));
          if (changedIndex !== -1) {
            didScroll = scrollToIndex(changedIndex);
          }
        }
      }

      if (!didScroll) {
        updateArrowsVisibility();
      }
    });
  },
  { deep: true, flush: 'post' },
);

onMounted(() => {
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  isTouchDevice.value = (isMobile.value || isTablet.value) && hasTouch;
  setTimeout(updateArrowsVisibility, 100);
});

onUnmounted(() => {
  if (scrollTimeout) clearTimeout(scrollTimeout);
});

defineExpose({
  scrollToIndex,
  scrollNext,
  scrollPrev,
  updateArrowsVisibility,
  viewport,
  track,
});
</script>

<style lang="scss" scoped>
.summary-carousel {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  &:hover {
    .carousel-arrow {
      opacity: 1;
    }
  }

  .carousel-slide {
    &:first-child {
      box-sizing: content-box;
      padding-left: var(--carousel-offset);
    }

    &:last-child {
      box-sizing: content-box;
      padding-right: var(--carousel-offset);
    }
  }
}

.carousel-viewport {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  position: relative;

  &::-webkit-scrollbar {
    display: none;
  }
}

.carousel-track {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  gap: var(--carousel-gap-desktop);
  min-width: 100%;

  @include breakpoint(small down) {
    gap: var(--carousel-gap-mobile);
  }
}

.carousel-slide {
  flex: 0 0 calc((100% - var(--visible-slides-desktop-gap-count) * var(--carousel-gap-desktop)) / var(--visible-slides-desktop));
  box-sizing: border-box;

  @include breakpoint(medium only) {
    flex: 0 0 calc((100% - var(--visible-slides-medium-gap-count) * var(--carousel-gap-mobile)) / var(--visible-slides-medium));
  }

  @include breakpoint(small down) {
    flex: 0 0 calc((100% - var(--visible-slides-mobile-gap-count) * var(--carousel-gap-mobile)) / var(--visible-slides-mobile));
  }
}

.carousel-slide-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.carousel-slide-button {
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  width: 100%;
  height: 100%;
  text-align: left;
  cursor: pointer;
  display: block;
  outline: none;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;

  &:focus-visible {
    box-shadow: inset 0 0 0 2px $black;
  }
}

.carousel-arrow {
  position: absolute;
  z-index: 10;
  top: 50%;
  transform: translateY(-50%);
  padding: 4px;
  border-radius: 50%;
  background: rgba($white, 0.85);
  cursor: pointer;
  transition: background 0.2s;

  opacity: 1;

  &:hover {
    background: $white;
  }

  &.prev {
    left: 8px;
  }

  &.next {
    right: 8px;
  }
}
</style>
